import { useCallback, useEffect, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import type { FilteredNav } from "./nav-config";

/** UXR-13: Simple (default) hides the pages flagged `advanced` from the nav; routes stay reachable. */
export type NavMode = "simple" | "advanced";

const KEY_PREFIX = "wato.nav.mode.";

export function readNavMode(userId: string): NavMode {
  try {
    return window.localStorage.getItem(KEY_PREFIX + userId) === "advanced" ? "advanced" : "simple";
  } catch {
    return "simple";
  }
}

function writeNavMode(userId: string, mode: NavMode): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + userId, mode);
  } catch {
    /* storage unavailable: the choice lasts until reload */
  }
}

/** Pure: in Simple mode drop advanced pages from every section (top items are untouched). */
export function applyNavMode(nav: FilteredNav, mode: NavMode): FilteredNav {
  if (mode === "advanced") return nav;
  const sections: FilteredNav["sections"] = {};
  for (const [to, pages] of Object.entries(nav.sections)) {
    sections[to] = pages.filter((p) => !p.advanced);
  }
  return { ...nav, sections };
}

/**
 * Per-user mode, stored on the user profile (`PATCH /v1/auth/me/profile { navMode }`).
 * `serverMode` (from `/me`) wins when present; localStorage is only an instant cache / fallback.
 */
export function useNavMode(
  userId: string,
  serverMode?: NavMode,
  save?: (m: NavMode) => Promise<unknown>,
): [NavMode, (m: NavMode) => void] {
  const [mode, setMode] = useState<NavMode>(() => serverMode ?? readNavMode(userId));
  useEffect(() => setMode(serverMode ?? readNavMode(userId)), [userId, serverMode]);
  const set = useCallback(
    (m: NavMode) => {
      writeNavMode(userId, m);
      setMode(m);
      void save?.(m)?.catch(() => undefined);
    },
    [userId, save],
  );
  return [mode, set];
}

export function NavModeToggle({
  mode,
  onChange,
}: {
  mode: NavMode;
  onChange: (m: NavMode) => void;
}) {
  const { t } = useI18n();
  const advanced = mode === "advanced";
  return (
    <div className="mt-3 px-3" data-testid="nav-mode-toggle">
      <button
        type="button"
        role="switch"
        aria-checked={advanced}
        title={t("dashboard.nav.mode.hint")}
        onClick={() => onChange(advanced ? "simple" : "advanced")}
        className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md text-xs font-semibold text-app-rail-muted hover:text-app-rail-fg focus-visible:outline-2 focus-visible:outline-app-rail-fg"
      >
        <span>{t(advanced ? "dashboard.nav.mode.advanced" : "dashboard.nav.mode.simple")}</span>
        <span aria-hidden="true">{advanced ? "●" : "○"}</span>
      </button>
    </div>
  );
}
